import { useEffect, useRef, useState } from "react";
import { useParams, useRouteLoaderData, useSubmit } from "react-router-dom";
import { field, FieldType, FormRule, SelectOption } from "../../types";
import { validateFields, validateRules } from "./validation";
import { ORDER_STEP } from "./dnd";
export function useFormBuilder() {
	const formRef = useRef<HTMLFormElement | null>(null);
	const submit = useSubmit();
	const params = useParams<{ id?: string }>();
	const loaderData = useRouteLoaderData("form") as any;
	const isEdit = !!loaderData?.form;
	const [formName, setFormName] = useState("");
	const [formType, setFormType] = useState("");
	const [fields, setFields] = useState<field[]>([]);
	const [rules, setRules] = useState<FormRule[]>([]);
	function addRule() {
		setRules(prev => [
			...prev,
			{
				id: crypto.randomUUID(),
				when: [
					{
						field_key: -1,
						operator: "eq",
						value: ""
					}
				],
				then: {
					show: []
				}
			}
		]);
	}
	function updateRule(ruleId: string, patch: Partial<FormRule>) {
		setRules(prev =>
			prev.map(r => (r.id === ruleId ? { ...r, ...patch } : r))
		);
	}
	function removeRule(ruleId: string) {
		setRules(prev => prev.filter(r => r.id !== ruleId));
	}
	useEffect(() => {
		if (!isEdit) return;
		setFormName(loaderData.form.name);
		setFormType(loaderData.form.type);
		setFields(loaderData.fields ?? []);
		if(loaderData.form.rules){
			try {
				const parsed = typeof loaderData.form.rules === "string" ? JSON.parse(loaderData.form.rules) : loaderData.form.rules;
				if(Array.isArray(parsed)){
					setRules(parsed);
				}else{
					setRules([]);
				}
			} catch (e) {
				console.error("Failed to parse rules", e);
				setRules([]);
			}
		} else {
			setRules([]);
		}
	}, [isEdit, loaderData]);
	function addField(type: FieldType = "text") {
		setFields(prev => [
			...prev,
			{
				id: -1, 
				field_key: getNextFieldKey(prev),
				form_id: Number(params.id) ?? -1,
				label: "",
				field_type: type,
				is_required: 0 as 1 | 0,
				sort_order: (prev.length + 1) * ORDER_STEP,
				options: type === "select" ? [] : null,
				default_value: null,
				sub_form_id: null, 
				sub_form_name: null, 
				min: null, 
				max: null, 
				placeholder: null
			}
		]);
	}
	function getNextFieldKey(fields: { field_key: number }[]): number {
		const used = new Set(fields.map(f => f.field_key));
		let i = 0;
		while(used.has(i)){
			i++;
		}
		return i;
	}
	function updateField(field_key: number, patch: Partial<field>) {
		setFields(prev =>
			prev.map(f => (f.field_key === field_key ? { ...f, ...patch } : f))
		);
  	}
	function getField(field_key: number) {
		return fields.find(f => f.field_key === field_key) ?? null;
	}
	function removeField(field_key: number) {
		setFields(prev => prev.filter(f => f.field_key !== field_key));
	}
	function submitForm(publish: boolean) {
		const fieldsError = validateFields(fields);
		if (fieldsError) return alert(fieldsError);
		const rulesError = validateRules(rules, fields);
		if (rulesError) return alert(rulesError);
		const payload = {
			form: {
				...(isEdit && { id: Number(params.id) }),
				name: formName,
				type: formType,
				rules: JSON.stringify(rules),  /* no transform needed, already clean JSON */
				status: publish ? "published" : "draft"
			},
			fields: fields.map(f => ({
				...f, 
				options: f.options ? JSON.stringify(f.options) : null, 
				default_value: 
				(f.default_value && ["user"].includes(f.field_type)) ? (f.default_value as SelectOption).value: 
				(f.default_value && ["users"].includes(f.field_type)) ? JSON.stringify((f.default_value as SelectOption[]).map((item:SelectOption) => item.value)): 
				(f.default_value && ["checkbox", "multiSelect"].includes(f.field_type)) ? JSON.stringify((f.default_value as SelectOption[])): 
				f.default_value
			})), 
		};
		if (!formRef.current?.reportValidity()) return;
		const fd = new FormData();
		fd.append("payload", JSON.stringify(payload));
		submit(fd, { method: "post" });
	}
	return {
		formRef,
		isEdit,
		formName,
		setFormName,
		formType,
		setFormType,
		fields,
		setFields,
		addField,
		updateField,
		getField, 
		removeField,
		submitForm, 
		addRule, 
		updateRule, 
		removeRule, 
		rules
	};
}